import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFollowCursor } from '../hooks/useFollowCursor';
import arrow from '../assets/icons/arrow-right-white.svg';

interface StripeDonationFormProps {
  gradient?: 'blue' | 'orange';
}

export default function StripeDonationForm({ gradient = 'blue' }: StripeDonationFormProps) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const buttonProps = useFollowCursor();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount < 1) return;
    setLoading(true);

    try {
      const res = await fetch('/api/create-stripe-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: numericAmount }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('API Response Error:', res.status, errorText);
        throw new Error(`Server error: ${res.status}`);
      }

      const data = await res.json();
      setLoading(false);

      if (data.url) {
        // Validate Stripe checkout URL before redirecting
        try {
          const url = new URL(data.url);
          if (url.hostname === 'checkout.stripe.com' || url.hostname === 'buy.stripe.com') {
            window.location.href = data.url;
          } else {
            console.error('Invalid redirect URL:', data.url);
            alert('Invalid payment URL received. Please try again.');
          }
        } catch {
          console.error('Malformed URL:', data.url);
          alert('Invalid payment URL received. Please try again.');
        }
      } else {
        console.error('No URL in response:', data);
        alert(data.error || 'Error creating Stripe session');
      }
    } catch (error) {
      console.error('Stripe session error:', error);
      setLoading(false);
      alert('Error connecting to payment system. Please try again.');
    }
  };

  const isButtonDisabled = loading || !amount || Number(amount) < 1;

  // Button background classes
  const buttonBg = isButtonDisabled
    ? 'bg-gray-300'
    : gradient === 'orange'
      ? 'bg-gradient-to-r from-orange-500 to-orange-300'
      : 'bg-gradient-to-r from-blue-600 to-blue-400';

  return (
    <div className="w-full flex justify-center">
      <div className="w-full max-w-[600px] bg-white rounded-lg p-[1.25em]">
        <h3 className="body-large mb-[15px]">Donación Mensual</h3>
        <p className="body mb-[30px] text-gray-700">
          Apoya de manera recurrente a La Gaviota IAP y ayuda a que más niñas y niños reciban una educación integral y exclusiva. Puedes elegir el monto mensual que deseas aportar.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 w-full">
            <label className="body font-medium text-gray-700 mb-1" htmlFor="stripe-donation-amount">
              Donación mensual (MXN):
            </label>
            <div className="flex flex-col md:flex-row gap-4 w-full items-stretch">
              <div className="relative w-full md:w-24 flex items-stretch">
                <input
                  id="stripe-donation-amount"
                  type="number"
                  min={1}
                  value={amount}
                  onChange={e => setAmount(e.target.value.replace(/^0+(?!$)/, ''))}
                  className="border rounded px-2 pr-6 py-[0.9375em] w-full h-full body focus:outline-none focus:ring-2 focus:ring-black appearance-none placeholder:text-gray-400 placeholder:italic"
                  style={{ MozAppearance: 'textfield' }}
                  onWheel={e => (e.target as HTMLInputElement).blur()}
                  placeholder="200"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none select-none">$</span>
                <style>{`
                  input[type=number]::-webkit-inner-spin-button, 
                  input[type=number]::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                  }
                  input[type=number] {
                    appearance: textfield;
                  }
                `}</style>
              </div>
              <motion.div ref={buttonProps.ref} style={buttonProps.style} className="flex-1 flex items-stretch">
                <motion.button
                  type="submit"
                  disabled={isButtonDisabled}
                  className={`w-full flex justify-between items-center px-[1.25em] py-[0.9375em] rounded-lg text-white no-underline body-medium transition-all duration-300 ${buttonBg} focus:outline-none focus:ring-0 hover:outline-none hover:ring-0 active:outline-none active:ring-0 h-full`}
                  style={{ minWidth: 0, boxShadow: 'none', border: 'none' }}
                  layout
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {isButtonDisabled ? (
                      <motion.span
                        key="disabled"
                        className="mx-auto"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        Inserta una cantidad
                      </motion.span>
                    ) : (
                      <>
                        <motion.span
                          key="enabled"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {loading ? 'Redirigiendo...' : 'Aporta a la causa'}
                        </motion.span>
                        <motion.img
                          key="arrow"
                          src={arrow}
                          alt="→"
                          className="w-6 h-6 ml-2"
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.2 }}
                        />
                      </>
                    )}
                  </AnimatePresence>
                </motion.button>
              </motion.div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
} 